// ATTICUS-ANALYTICS-COMMAND-CENTER-1 — Operations / COO lens.
//
// Turnaround, closing ratio + backlog-growth signal, backlog aging, bill-review
// bottlenecks, task aging, and (team-gated) caseload with neutral capacity
// framing — never a productivity leaderboard.
import React, { useEffect, useState, useMemo } from "react";
import { apiFetch } from "../../api";
import { KpiCard, ChartCard, buildQS } from "../AnalyticsKit";
import { Donut, HBar, TONE_HEX } from "../charts";

export default function OperationsLens({ filters, openDrill, reloadKey }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let live = true;
    setLoading(true); setError(null);
    apiFetch(`/analytics/operations?${buildQS(filters)}`)
      .then((d) => { if (live) { setData(d); setLoading(false); } })
      .catch((e) => { if (live) { setError(e.message || String(e)); setLoading(false); } });
    return () => { live = false; };
  }, [filters, reloadKey]);

  const cr = data?.closing_ratio || {};
  const ta = data?.task_aging || {};
  const turn = useMemo(() => (data?.turnaround || []).map((r) => ({ name: r.bucket, value: r.count })), [data]);
  const backlog = useMemo(() => (data?.backlog_aging || []).map((r) => ({ name: r.bucket, value: r.count })), [data]);
  const bills = useMemo(() => (data?.bill_stages || []).map((r) => ({ name: r.stage, value: r.count })), [data]);
  const caseload = useMemo(() => (data?.caseload || []).map((r) => ({ name: r.name, value: r.count, _id: r.adjuster_id })), [data]);
  const tasks = useMemo(() => ([
    { name: "Overdue", value: ta.overdue || 0, color: TONE_HEX.red },
    { name: "Due ≤7d", value: ta.due_7d || 0, color: TONE_HEX.amber },
    { name: "Later", value: ta.later || 0, color: TONE_HEX.blue },
  ].filter((d) => d.value > 0)), [ta]);

  if (error) return <div className="an-error">Couldn't load operations analytics: {error}</div>;
  const L = loading && !data;

  return (
    <div className="an-stack">
      <div className="an-kpis" style={{ gridTemplateColumns: "repeat(4,1fr)" }}>
        <KpiCard label="Closing Ratio" tone={cr.growing_backlog ? "red" : "green"}
                 value={L ? "…" : (cr.ratio != null ? `${cr.ratio}×` : "—")}
                 sub={L ? "" : `${cr.closed ?? 0} closed / ${cr.opened ?? 0} opened${cr.growing_backlog ? " — backlog growing" : ""}`} />
        <KpiCard label="Avg Turnaround" value={L ? "…" : `${data?.turnaround_avg_days ?? "—"} d`}
                 sub={L ? "" : `${data?.turnaround_n ?? 0} closed in period`} />
        <KpiCard label="Open Tasks" value={L ? "…" : num(ta.total)} sub="all assignees" />
        <KpiCard label="Overdue Tasks" tone={ta.overdue > 0 ? "red" : undefined}
                 value={L ? "…" : num(ta.overdue)} sub={ta.overdue ? "need attention" : "none overdue"} />
      </div>

      <div className="an-grid">
        <ChartCard title="Claim Turnaround Time" subtitle="days opened→closed (closed in period); click a bucket"
                   loading={L} empty={!turn.some((d) => d.value > 0)}>
          <HBar data={turn} onSegmentClick={(p) => openDrill("turnaround_bucket", p.name, `Closed in ${p.name} days`)} color="#3b6fd4" />
        </ChartCard>

        <ChartCard title="Backlog Aging" subtitle="open claims by age; click a bucket"
                   loading={L} empty={!backlog.some((d) => d.value > 0)}>
          <HBar data={backlog} onSegmentClick={(p) => openDrill("reserve_age_bucket", p.name, `Open claims aged ${p.name} days`)} color="#0ea5e9" />
        </ChartCard>

        <ChartCard title="Bill Review Bottlenecks" subtitle="bills by workflow stage; click to drill"
                   loading={L} empty={!bills.length}>
          <HBar data={bills} onSegmentClick={(p) => openDrill("bill_stage", p.name, `Bills: ${p.name}`)} color="#f59e0b" />
        </ChartCard>

        <ChartCard title="Task Aging" subtitle="open tasks by urgency" loading={L} empty={!tasks.length}>
          <Donut data={tasks} />
        </ChartCard>

        {data?.caseload_gated ? (
          <ChartCard title="Caseload by Adjuster" wide
                     unavailable={{ status: "Restricted", reason: "Team caseload is visible to managers/executives only (view_team_reports).", dependency: "VIEW_TEAM_REPORTS" }} />
        ) : (
          <ChartCard title="Caseload by Adjuster" subtitle="open claims per lead adjuster (capacity view — not a ranking)" wide
                     loading={L} empty={!caseload.length}
                     height={Math.max(260, caseload.length * 26)}>
            <HBar data={caseload} height={Math.max(240, caseload.length * 26)}
                  onSegmentClick={(p) => p._id && openDrill("adjuster_caseload", String(p._id), `${p.name} — open claims`)} color="#6366f1" />
          </ChartCard>
        )}

        <ChartCard title="Document Review Bottlenecks"
                   unavailable={{ status: "Deferred", reason: data?.deferred?.document_review, dependency: "inbound-email ingestion" }} />
      </div>
    </div>
  );
}

function num(n) { return n == null ? "—" : Number(n).toLocaleString(); }
